import React from 'react';
import SEO from '@/components/seo/SEO';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ShieldCheck, Database, UserCheck, ShieldAlert, Lock, Mail, Eye, MapPin, CheckCircle2 } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  const lastUpdated = "January 5, 2026";
  const companyName = "RunOffTrack.com";
  const contactEmail = "danielgaskins99@gmail.com";

  const sections = [
    { id: 'collection', label: '1. Information We Collect' },
    { id: 'usage', label: '2. How We Use Data' },
    { id: 'storage', label: '3. Storage & Security' },
    { id: 'sharing', label: '4. Third-Party Sharing' },
    { id: 'rights', label: '5. Your Privacy Rights' },
    { id: 'cookies', label: '6. Cookies & Tracking' },
    { id: 'children', label: '7. Children\'s Privacy' },
    { id: 'contact', label: '8. Contact Us' },
  ];

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/20">
      <SEO 
        title="Privacy Policy - RunOffTrack.com"
        description="Learn how RunOffTrack.com collects, uses, and protects your personal and project data."
        canonical="/privacy-policy"
      />

      {/* Hero Section */}
      <div className="bg-muted/30 pt-32 pb-20 border-b border-border">
        <div className="container px-4 text-center max-w-4xl mx-auto">
           <Breadcrumbs items={[{ label: 'Legal', href: '/privacy-policy' }]} />
           
           <div className="inline-flex items-center justify-center p-3 bg-primary/10 text-primary rounded-full mb-6">
             <ShieldCheck size={32} />
           </div>
           <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4 text-foreground">
             Privacy Policy
           </h1>
           <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
             Your data privacy is the foundation of our compliance platform. We are committed to protecting the information you entrust to us.
           </p>
           <div className="mt-6 text-xs font-bold uppercase tracking-widest text-muted-foreground/60">
             Last Updated: {lastUpdated}
           </div>
        </div>
      </div>

      <div className="container px-4 py-16 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[250px_1fr] gap-16 items-start">
          
          {/* Sidebar Navigation */}
          <aside className="hidden lg:block sticky top-24 space-y-4 border-r border-border pr-8">
            <p className="text-xs font-bold uppercase text-muted-foreground tracking-tighter mb-6">Policy Sections</p>
            <nav className="flex flex-col space-y-3">
              {sections.map(s => (
                <a 
                  key={s.id} 
                  href={`#${s.id}`} 
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <article className="prose prose-slate dark:prose-invert max-w-none space-y-16">
            
            <section id="collection" className="scroll-mt-24">
              <h2 className="text-2xl font-bold flex items-center gap-3 text-foreground">
                <div className="p-2 bg-muted rounded-lg"><Database size={20}/></div>
                1. Information We Collect
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We collect information that you provide directly to us, information we collect automatically when you use our Service, and information from third parties.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <Card className="bg-card">
                  <CardContent className="p-4">
                    <p className="font-bold text-sm mb-1 flex items-center gap-2">
                        <UserCheck size={16} className="text-primary"/> Personal Data
                    </p>
                    <p className="text-xs text-muted-foreground">Name, email address, billing information, and professional certifications.</p>
                  </CardContent>
                </Card>
                <Card className="bg-card">
                  <CardContent className="p-4">
                    <p className="font-bold text-sm mb-1 flex items-center gap-2">
                        <MapPin size={16} className="text-primary"/> Project Data
                    </p>
                    <p className="text-xs text-muted-foreground">Site locations, permit numbers, inspection photos, and compliance logs.</p>
                  </CardContent>
                </Card>
              </div>
            </section>

            <Separator />

            <section id="usage" className="scroll-mt-24">
              <h2 className="text-2xl font-bold flex items-center gap-3 text-foreground">
                <div className="p-2 bg-muted rounded-lg"><Eye size={20}/></div>
                2. How We Use Data
              </h2>
              <p className="text-muted-foreground">
                RunOffTrack.com uses the collected data for various purposes:
              </p>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-muted-foreground list-none p-0">
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-green-500"/> Providing and maintaining the Service</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-green-500"/> Generating legal compliance reports</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-green-500"/> Processing transactions via Stripe</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-green-500"/> Detecting and preventing technical issues</li>
              </ul>
            </section>

            <section id="storage" className="scroll-mt-24">
              <h2 className="text-2xl font-bold flex items-center gap-3 text-foreground">
                <div className="p-2 bg-muted rounded-lg"><Lock size={20}/></div>
                3. Storage & Security
              </h2>
              <p className="text-muted-foreground">
                Your data is stored securely using Google Cloud Platform (Firebase) infrastructure. We implement industry-standard encryption at rest (AES-256) and in transit (TLS/SSL).
              </p>
              <div className="bg-primary/5 p-6 rounded-2xl border border-primary/10">
                <p className="text-sm m-0 font-medium italic text-primary">
                    "While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security."
                </p>
              </div>
            </section>

            <section id="sharing" className="scroll-mt-24">
              <h2 className="text-2xl font-bold text-foreground">4. Third-Party Sharing</h2>
              <p className="text-muted-foreground">
                We do not sell your personal data. We only share information with service providers necessary to operate our business:
              </p>
              <div className="space-y-4">
                {[
                  { provider: 'Google Cloud / Firebase', usage: 'Cloud infrastructure and database hosting.' },
                  { provider: 'Stripe', usage: 'Payment processing and billing management.' },
                  { provider: 'SendGrid', usage: 'Automated email delivery for inspection reports.' },
                  { provider: 'Open-Meteo', usage: 'Weather and rainfall data retrieval.' }
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-center p-3 rounded-lg border bg-muted/20">
                    <span className="text-sm font-bold">{item.provider}</span>
                    <span className="text-xs text-muted-foreground">{item.usage}</span>
                  </div>
                ))}
              </div>
            </section>

            <section id="rights" className="scroll-mt-24">
              <h2 className="text-2xl font-bold flex items-center gap-3 text-foreground">
                <div className="p-2 bg-muted rounded-lg"><ShieldAlert size={20}/></div>
                5. Your Privacy Rights
              </h2>
              <p className="text-muted-foreground">
                Depending on your location (such as the EU or California), you may have rights under the GDPR or CCPA, including:
              </p>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• <strong>The right to access</strong> – You can request copies of your personal data.</li>
                <li>• <strong>The right to rectification</strong> – You can request that we correct inaccurate information.</li>
                <li>• <strong>The right to erasure</strong> – You can request that we delete your data under certain conditions.</li>
              </ul>
            </section>

            <section id="contact" className="scroll-mt-24 pt-12">
              <div className="bg-slate-900 text-white p-8 rounded-3xl text-center">
                 <Mail className="mx-auto mb-4 text-primary" size={32} />
                 <h3 className="text-2xl font-bold mb-2">Privacy Inquiries</h3>
                 <p className="text-slate-400 mb-6">To exercise your data rights or ask questions about this policy, contact our Data Protection Officer.</p>
                 <a 
                    href={`mailto:${contactEmail}`} 
                    className="inline-block bg-primary text-white font-bold py-3 px-8 rounded-full hover:bg-primary/90 transition-colors"
                  >
                   {contactEmail}
                 </a>
              </div>
            </section>

          </article>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
